/*
 * GitHub-style identicon for players without an uploaded avatar: a 5×5 pixel
 * grid, mirrored left-to-right so it reads as a sprite rather than noise. The
 * name hash drives both the pattern (15 bits: 5 rows × 3 unique columns) and
 * the color, so a player keeps the same tile on every screen with nothing
 * stored.
 *
 * Color: a hue anywhere on the OKLCH wheel at one fixed lightness and chroma.
 * OKLCH (not HSL) so every hue looks equally bright - no washed-out yellows or
 * near-black blues - and L 0.6 keeps ~3:1 against both the dark (#2c3547) and
 * light (#e6dcc6) panel-in tile behind it. Hues the sRGB gamut can't reach at
 * this chroma (teals, ambers) are mapped in by the browser.
 */

const GRID = 5;
/** Columns 0..2 are drawn from the hash; 3 and 4 mirror 1 and 0. */
const HALF = Math.ceil(GRID / 2);
/** Empty margin around the grid, in cells. */
const PADDING = 0.5;
const PATTERN_BITS = GRID * HALF;
const PATTERN_MASK = (1 << PATTERN_BITS) - 1;
/** Row 2, column 2 - lit when the hash leaves the grid empty (1 in 32768). */
const CENTER_CELL = 1 << (2 * HALF + 2);

const LIGHTNESS = 0.6;
const CHROMA = 0.14;
const HUE_DEGREES = 360;

export const IDENTICON_VIEWBOX = `0 0 ${GRID + 2 * PADDING} ${GRID + 2 * PADDING}`;

export interface Identicon {
  /** CSS color of the lit cells. */
  color: string;
  /** SVG path of every lit cell, in {@link IDENTICON_VIEWBOX} units. */
  path: string;
}

/**
 * FNV-1a with murmur3's finalizer: FNV alone leaves the high bits poorly mixed
 * for short names, and both the pattern (low bits) and hue (high bits) need
 * them independent.
 */
function hashName(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i += 1) {
    hash ^= name.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;

  return hash >>> 0;
}

function cell(column: number, row: number): string {
  return `M${column + PADDING} ${row + PADDING}h1v1h-1z`;
}

function patternPath(pattern: number): string {
  const cells: string[] = [];

  for (let row = 0; row < GRID; row += 1) {
    for (let column = 0; column < HALF; column += 1) {
      if ((pattern >>> (row * HALF + column)) & 1) {
        const mirror = GRID - 1 - column;
        cells.push(cell(column, row));
        if (mirror !== column) cells.push(cell(mirror, row));
      }
    }
  }

  return cells.join('');
}

export function identicon(name: string): Identicon {
  // Case-folded: screens pass either the in-game name or its display casing,
  // and "Yorki" / "yorki" are one player in the DB.
  const hash = hashName(name.toLowerCase());
  const pattern = hash & PATTERN_MASK || CENTER_CELL;
  const hue = (hash >>> PATTERN_BITS) % HUE_DEGREES;

  return {
    color: `oklch(${LIGHTNESS} ${CHROMA} ${hue})`,
    path: patternPath(pattern),
  };
}
